import React from "react";
import { Box, Grid, Typography, Button, Container } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import LocalCarWashIcon from "@mui/icons-material/LocalCarWash";
import SecurityIcon from "@mui/icons-material/Security";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PrimeMembership from "../common/Breakdown";

const DentingServiceOverview = () => {
  // Setup InView hooks for animations
  const { ref: heroRef, inView: heroInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: servicesRef, inView: servicesInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: howItWorksRef, inView: howItWorksInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: ctaRef, inView: ctaInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // Blow-Out animation: starts small and expands
  const blowOutAnimation = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const whatsappNumber = "9133959551"; // Replace with your WhatsApp number
  const message = "Hello! I would like to get more information.";

  // Function to navigate to WhatsApp with a pre-filled message
  const handleClick = () => {
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };


  return (
    <Box sx={{ backgroundColor: "#ffffff", overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          background: `linear-gradient(135deg, #ff5722 30%, #ffc107 100%)`,
          position: "relative",
          color: "white",
          padding: { xs: "5rem 1rem", md: "7rem 1rem" },
          clipPath: "polygon(0 0, 100% 0, 100% 85%, 0% 100%)",
        }}
      >
        {heroInView && (
          <motion.div
            variants={blowOutAnimation}
            initial="hidden"
            animate="visible"
          >
            <Container maxWidth="lg">
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  textAlign: "center",
                  mb: 3,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                Introducing Go Clutch Prime Membership – Only ₹1499 for 12
                Months!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  maxWidth: "700px",
                  mx: "auto",
                  mb: 4,
                }}
              >
                Take your car care to the next level and enjoy savings up to
                ₹24,000! 🎉 With our Prime Membership, get exclusive benefits
                designed to keep your car running smoothly all year long.
              </Typography>
              <Box textAlign="center">
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleClick}
                    sx={{
                      backgroundColor: "white",
                      color: "#ff5722",
                      padding: { xs: "0.8rem 2rem", md: "1rem 3rem" },
                      fontSize: { xs: "1rem", md: "1.3rem" },
                      borderRadius: "50px",
                      "&:hover": { backgroundColor: "#ffcc80" },
                    }}
                  >
                    Join Membership Now
                  </Button>
                </motion.div>
              </Box>
            </Container>
          </motion.div>
        )}
      </Box>

      {/* Services Importance Section */}
      <Box
        ref={servicesRef}
        sx={{
          backgroundColor: "#f9f9f9",
          padding: { xs: "2rem 0", md: "4rem 0" },
        }}
      >
        <Container sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          {servicesInView && (
            <motion.div
              variants={blowOutAnimation}
              initial="hidden"
              animate="visible"
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: { xs: 3, md: 4 } }}
              >
                Why Car Painting is Essentail
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={6} sm={3}>
                  <motion.div whileHover={{ scale: 1.1 }}>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        padding: "1.5rem",
                        borderRadius: "50%",
                        textAlign: "center",
                        width: "100px",
                        height: "100px",
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <LocalCarWashIcon
                        sx={{ fontSize: 40, color: "#ff5722" }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                      Rust Prevention
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: { xs: "none", sm: "block" }, mt: 1 }}
                    >
                      Paint protects metal from rust
                    </Typography>
                  </motion.div>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <motion.div whileHover={{ scale: 1.1 }}>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        padding: "1.5rem",
                        borderRadius: "50%",
                        textAlign: "center",
                        width: "100px",
                        height: "100px",
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <BuildCircleIcon
                        sx={{ fontSize: 40, color: "#ff5722" }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                      Increased Value
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: { xs: "none", sm: "block" }, mt: 1 }}
                    >
                      A good paint job boosts resale value.
                    </Typography>
                  </motion.div>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <motion.div whileHover={{ scale: 1.1 }}>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        padding: "1.5rem",
                        borderRadius: "50%",
                        textAlign: "center",
                        width: "100px",
                        height: "100px",
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <LocalCarWashIcon
                        sx={{ fontSize: 40, color: "#ff5722" }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                      Environmental Shield
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: { xs: "none", sm: "block" }, mt: 1 }}
                    >
                      Guards against debris and weather.
                    </Typography>
                  </motion.div>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <motion.div whileHover={{ scale: 1.1 }}>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        padding: "1.5rem",
                        borderRadius: "50%",
                        textAlign: "center",
                        width: "100px",
                        height: "100px",
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <EmojiEventsIcon
                        sx={{ fontSize: 40, color: "#ff5722" }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                      UV Protection
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: { xs: "none", sm: "block" }, mt: 1 }}
                    >
                      Prevents fading from sunlight.
                    </Typography>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          )}
        </Container>
      </Box>

      {/* How Periodic Services Work */}
      <Box
        ref={howItWorksRef}
        sx={{
          backgroundColor: "#fff",
          padding: { xs: "2rem 0", md: "4rem 0" },
        }}
      >
        <Container>
          {howItWorksInView && (
            <motion.div
              variants={blowOutAnimation}
              initial="hidden"
              animate="visible"
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  textAlign: "center",
                  mb: { xs: 4, md: 6 },
                }}
              >
                How Our Periodic Services Work
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div whileHover={{ scale: 1.05 }}>
                    <Box
                      sx={{
                        backgroundColor: "#f9f9f9",
                        padding: { xs: "1rem", md: "2rem" },
                        borderRadius: "12px",
                        textAlign: "center",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        1. Initial Inspection
                      </Typography>
                      <Typography variant="body2">
                        We thoroughly inspect your vehicle to detect any issues
                        early.
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div whileHover={{ scale: 1.05 }}>
                    <Box
                      sx={{
                        backgroundColor: "#f9f9f9",
                        padding: { xs: "1rem", md: "2rem" },
                        borderRadius: "12px",
                        textAlign: "center",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        2. Maintenance
                      </Typography>
                      <Typography variant="body2">
                        We perform essential services, from oil changes to brake
                        checks.
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div whileHover={{ scale: 1.05 }}>
                    <Box
                      sx={{
                        backgroundColor: "#f9f9f9",
                        padding: { xs: "1rem", md: "2rem" },
                        borderRadius: "12px",
                        textAlign: "center",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        3. Final Check
                      </Typography>
                      <Typography variant="body2">
                        After maintenance, we conduct a final check to ensure
                        everything is optimal.
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          )}
        </Container>
      </Box>
      <Box>
        <PrimeMembership />
      </Box>

      {/* Final Call to Action */}
      <Box
        ref={ctaRef}
        sx={{
          backgroundColor: "#ff5722",
          padding: "4rem 0",
          color: "white",
          textAlign: "center",
          clipPath: "polygon(0 15%, 100% 0, 100% 100%, 0 100%)",
        }}
      >
        {ctaInView && (
          <motion.div
            variants={blowOutAnimation}
            initial="hidden"
            animate="visible"
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              Get Ahead with Regular Servicing
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, maxWidth: "700px", mx: "auto" }}
            >
              Stay ahead of the curve by booking your next periodic service now
              and avoid costly repairs later.
            </Typography>
            {/* <motion.div whileHover={{ scale: 1.1 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "white",
                  color: "#ff5722",
                  padding: "1rem 3rem",
                  borderRadius: "50px",
                  fontSize: "1.3rem",
                  "&:hover": { backgroundColor: "#ffcc80" },
                }}
              >
                Book Now
              </Button>
            </motion.div> */}
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default DentingServiceOverview;
